import React, { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { gql, useMutation, useQuery, useSuspenseQuery } from '@apollo/client';
import { TextInput } from '../common/TextInput';
import Button from '../common/Button';
import { Modal, useModalContext } from '../common/Modal';
import { toast } from 'react-toastify';
import { MdClose } from 'react-icons/md';
import { SupplierProductManager_GetSupplierProductQuery, SupplierProductManager_GetSupplierProductQueryVariables } from '../../../codegen/graphql';
import { GET_SUPPLIER_PRODUCT, UPDATE_SUPPLIER_PRODUCT, CREATE_SUPPLIER_PRODUCT_VARIATION_TYPE, UPDATE_SUPPLIER_PRODUCT_VARIATION_TYPE, DELETE_SUPPLIER_PRODUCT_VARIATION_TYPE, CREATE_SUPPLIER_PRODUCT_VARIATION_TYPE_VALUE, UPDATE_SUPPLIER_PRODUCT_VARIATION_TYPE_VALUE, DELETE_SUPPLIER_PRODUCT_VARIATION_TYPE_VALUE, GENERATE_SUPPLIER_PRODUCT_VARIATIONS, UPDATE_SUPPLIER_PRODUCT_VARIATION, DELETE_SUPPLIER_PRODUCT_VARIATION } from './SupplierProductManager.queries';
import { useModalRouter } from '../../ModalRouter_context';
import { DimensionsAndWeight, DimensionsAndWeightPopover } from '../common/DimensionsPopover';
import LockedTextInput from '../common/LockedTextInput';
import { useRequestManager } from '../common/RequestManager';
import { formatCurrency } from '../../utils/currency';
import { getTranslatedValue } from '../../../common/getTranslatedValue';


interface SupplierProductManagerProps {
  onClose: () => void;
  supplierProductId?: string;
}

export function SupplierProductManager({
  onClose,
  supplierProductId
}: SupplierProductManagerProps) {
  const modalRouter = useModalRouter();
  return (
    <Modal
      title={`Gestion ${supplierProductId}`}
      onClose={() => modalRouter.pop()}
      width="fullscreen"
    >
      <SupplierProductManagerContent
        supplierProductId={supplierProductId!}
      />
    </Modal>
  )
}

function SupplierProductManagerContent({
  supplierProductId
}: {
  supplierProductId: string;
}) {
  const { trackRequest } = useRequestManager();
  // Form states
  const [typeInput, setTypeInput] = useState('');
  const [valueInput, setValueInput] = useState('');
  const modalContext = useModalContext();

  const { data, refetch } = useSuspenseQuery<SupplierProductManager_GetSupplierProductQuery, SupplierProductManager_GetSupplierProductQueryVariables>(
    GET_SUPPLIER_PRODUCT, {
    variables: { id: supplierProductId },
  });

  const supplierProduct = data.supplierProduct;
  if (!supplierProduct) {
    throw new Error('Supplier product not found');
  }
  
  useLayoutEffect(() => {
    modalContext.setTitle(getTranslatedValue(supplierProduct.name))
  }, [modalContext, supplierProduct.name])

  const [updateSupplierProduct] = useMutation(UPDATE_SUPPLIER_PRODUCT);
  const [createVariationType] = useMutation(CREATE_SUPPLIER_PRODUCT_VARIATION_TYPE);
  const [updateVariationType] = useMutation(UPDATE_SUPPLIER_PRODUCT_VARIATION_TYPE);
  const [deleteVariationType] = useMutation(DELETE_SUPPLIER_PRODUCT_VARIATION_TYPE);
  const [createVariationTypeValue] = useMutation(CREATE_SUPPLIER_PRODUCT_VARIATION_TYPE_VALUE);
  const [updateVariationTypeValue] = useMutation(UPDATE_SUPPLIER_PRODUCT_VARIATION_TYPE_VALUE);
  const [deleteVariationTypeValue] = useMutation(DELETE_SUPPLIER_PRODUCT_VARIATION_TYPE_VALUE);
  const [generateVariations] = useMutation(GENERATE_SUPPLIER_PRODUCT_VARIATIONS);
  const [updateVariation] = useMutation(UPDATE_SUPPLIER_PRODUCT_VARIATION);
  const [deleteVariation] = useMutation(DELETE_SUPPLIER_PRODUCT_VARIATION);

  const refetchWithTransition = useCallback(async () => {
    startTransition(() => {
      refetch();
    });
  }, [refetch]);

  const handleChangeType = async (typeId: string, value: string) => {
    await trackRequest(
      updateVariationType({
        variables: { id: typeId, input: { type: value } },
      }), {
        title: `Modification du type "${value}"`,
        isBlocking: false,
      }
    );
    refetchWithTransition();
  };

  const handleTypeInputSubmit = async () => {
    if (!typeInput.trim()) return;

    await trackRequest(
      createVariationType({
        variables: {
          input: {
            supplierProductId: supplierProductId,
            type: typeInput
          }
        }
      }), {
        title: `Ajout du type "${typeInput}"`,
        isBlocking: false,
      }
    );
    setTypeInput('');
    refetchWithTransition();
  };

  const handleChangeTypeValue = async (_typeId: string, typeValueId: string, value: string) => {
    await trackRequest(
      updateVariationTypeValue({
        variables: { id: typeValueId, input: { value } },
      }), {
        title: `Modification de la valeur "${value}"`,
        isBlocking: false,
      }
    );
    refetchWithTransition();
  };

  const handleValueInputSubmit = async (typeId: string) => {
    if (!valueInput.trim()) return;

    await trackRequest(
      createVariationTypeValue({
        variables: {
          input: {
            typeId: typeId,
            value: valueInput
          }
        }
      }), {
        title: `Ajout de la valeur "${valueInput}"`,
        isBlocking: false,
      }
    );
    setValueInput('');
    refetchWithTransition();
  };

  const handleDeleteType = async (type: any) => {
    if (!confirm(`Voulez-vous vraiment supprimer le type "${getTranslatedValue(type.type)}" ?`)) {
      return;
    }

    await trackRequest(
      deleteVariationType({ variables: { id: type.id } }), {
      title: `Suppression du type "${getTranslatedValue(type.type)}"`,
      isBlocking: false,
    });
    setTypeInput('');
    refetchWithTransition();
  };

  const handleDeleteValue = async (value: any) => {
    await trackRequest(
      deleteVariationTypeValue({ variables: { id: value.id } }), {
        title: `Suppression de la valeur "${getTranslatedValue(value.value)}"`,
        isBlocking: false,
      }
    );
    refetchWithTransition();
  };

  const handleGenerateVariations = async () => {
    await trackRequest(
      generateVariations({
        variables: { supplierProductId: supplierProductId }
      }), {
        title: 'Génération des variations',
        isBlocking: false,
      }
    );
    refetchWithTransition();
  };

  const handleUpdateVariation = async (sku: string, field: string, value: any) => {
    await trackRequest(
      updateVariation({
        variables: {
          sku,
          supplierProductId: supplierProductId,
          input: { [field]: value }
        }
      }), {
        title: `Mise à jour de la variation "${sku}"`,
        isBlocking: false,
      }
    );
    refetchWithTransition();
  };


  const handleDeleteVariation = async (sku: string) => {
    if (!confirm(`Voulez-vous vraiment supprimer la variation ${sku} ?`)) {
      return;
    }

    // TODO: Do not delete if it is associated with an order line item
    await trackRequest(
      deleteVariation({
        variables: {
          sku,
          supplierProductId: supplierProductId
        }
      }), {
        title: `Suppression de la variation "${sku}"`,
        isBlocking: false,
      }
    );
    refetchWithTransition();
  };

  const variationTypes = supplierProduct.variationTypes;
  const variations = supplierProduct.variations;
  const numVariationTypeColumns = variationTypes.length;

  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(() => new Set());
  const [batchPackaging, setBatchPackaging] = useState<DimensionsAndWeight>({
    weight: 0,
    size1: 0,
    size2: 0,
    size3: 0,
  });

  const selectedCount = selectedSkus.size;
  const allSelected = variations.length > 0 && selectedCount === variations.length;
  const prevSelectedCountRef = useRef(0);

  useEffect(() => {
    if (selectedCount === 0) {
      prevSelectedCountRef.current = 0;
      return;
    }
    if (prevSelectedCountRef.current === 0) {
      const first = variations.find((v) => selectedSkus.has(v.sku));
      if (first) {
        setBatchPackaging({
          weight: first.packaging?.weight ?? 0,
          size1: first.packaging?.size1 ?? 0,
          size2: first.packaging?.size2 ?? 0,
          size3: first.packaging?.size3 ?? 0,
        });
      }
    }
    prevSelectedCountRef.current = selectedCount;
  }, [selectedCount, variations, selectedSkus]);

  const batchPopoverKey = useMemo(
    () => [...selectedSkus].sort().join('|'),
    [selectedSkus]
  );

  const toggleSkuSelected = (sku: string) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const toggleSelectAllVariations = () => {
    if (allSelected) {
      setSelectedSkus(new Set());
    } else {
      setSelectedSkus(new Set(variations.map((v) => v.sku)));
    }
  };

  const handleBatchPackaging = async () => {
    const skus = [...selectedSkus];
    for (const sku of skus) {
      await handleUpdateVariation(sku, 'packaging', batchPackaging);
    }
    setSelectedSkus(new Set());
  };

  return (<>
    <div className="flex flex-row gap-2">
      <p><strong>Supplier Type:</strong> {supplierProduct.supplierName}, Supplier product id: {supplierProduct.id}</p>
    </div>
    <TextInput
      label="Note"
      value={supplierProduct?.note || ''}
      onChange={(e) => {
        updateSupplierProduct({
          variables: {
            id: supplierProductId,
            input: { note: e.target.value }
          }
        });
      }}
      placeholder="Ajouter une note..."
    />

    <hr className="my-4" />

    <h2 className="text-lg font-medium text-gray-900 mb-4">Types et valeurs</h2>

    <div className="flex flex-row gap-2">
      {/* Left column: Types and values */}
      <div style={{ width: 450 }} className='mr-2'>
        <div className="flex items-center gap-2 mb-4">
          <TextInput
            label=""
            value={typeInput}
            onChange={(e) => setTypeInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleTypeInputSubmit()}
            placeholder="Nom du type"
          />
          <Button onClick={handleTypeInputSubmit} size="sm">
            Ajouter
          </Button>
        </div>

        <div className="space-y-4">
          {variationTypes.map((type: any) => (
            <div key={type.id} className="border rounded-lg overflow-hidden">
              {/* Type Header - same as VariationsSection */}
              <div className="bg-gray-50 px-4 py-3 flex items-center gap-4">
                <LockedTextInput
                  value={getTranslatedValue(type.type)}
                  onFinish={(value) => handleChangeType(type.id, value)}
                  size="lg"
                  className="font-bold"
                />
                <span className="text-sm text-gray-500">{type.typeValues?.length || 0} valeurs</span>
                <div className="flex-1" />
                <Button
                  preset="danger-light"
                  onClick={() => handleDeleteType(type)}
                >
                  <MdClose size={20} />
                </Button>
              </div>

              <div className="grid grid-cols-12 gap-2 px-2 py-2 bg-gray-50 border-b border-gray-200">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider col-span-1" />
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider col-span-8">
                  Valeur
                </div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider col-span-3">
                  Actions
                </div>
              </div>
              <div className="px-2 py-2 flex items-center gap-2 border-b border-gray-100">
                <TextInput
                  label=""
                  value={valueInput}
                  onChange={(e) => setValueInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleValueInputSubmit(type.id)}
                  placeholder="Valeur"
                  className="flex-1"
                />
                <Button onClick={() => handleValueInputSubmit(type.id)} size="sm">
                  Ajouter
                </Button>
              </div>
              <div className="divide-y divide-gray-200">
                {type.typeValues?.map((value: any) => (
                  <div
                    key={value.id}
                    className="grid grid-cols-12 gap-2 px-2 py-2 items-center hover:bg-gray-50"
                  >
                    <div className="col-span-1" />
                    <div className="col-span-8">
                      <LockedTextInput
                        value={getTranslatedValue(value.value)}
                        onFinish={(newValue) => handleChangeTypeValue(type.id, value.id, newValue)}
                      />
                    </div>
                    <div className="col-span-3">
                      <Button
                        preset="danger-light"
                        onClick={() => handleDeleteValue(value)}
                      >
                        <MdClose size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right column: Variations list */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">
            {variations.length} variations
          </h3>
          <Button onClick={handleGenerateVariations} preset="primary" size="sm">
            Générer toutes les variations
          </Button>
        </div>

        {variations.length > 0 ? (
          <div className="overflow-x-auto">
            {selectedCount > 0 ? (
              <div className="flex flex-wrap items-center gap-3 mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <DimensionsAndWeightPopover
                  key={batchPopoverKey}
                  dimensionsAndWeight={batchPackaging}
                  onChange={setBatchPackaging}
                />
                <Button onClick={handleBatchPackaging} preset="primary" size="sm">
                  Batch Packaging
                </Button>
              </div>
            ) : null}
            <div
              className="grid gap-2 px-3 py-3 bg-gray-50 border-b border-gray-200"
              style={{
                gridTemplateColumns: `minmax(36px, 44px) minmax(80px, 1fr) repeat(${numVariationTypeColumns}, minmax(60px, 1fr)) minmax(70px, 1fr) minmax(50px, 1fr) minmax(80px, 1fr) minmax(40px, 1fr)`
              }}
            >
              <div className="flex items-center justify-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={allSelected}
                  onChange={toggleSelectAllVariations}
                  aria-label="Sélectionner toutes les variations"
                />
              </div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</div>
              {variationTypes.map((t: any) => (
                <div key={t.id} className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {getTranslatedValue(t.type)}
                </div>
              ))}
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Coût</div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Dimensions</div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</div>
            </div>
            <div className="divide-y divide-gray-200">
              {variations.map((variation) => (
                <div
                  key={variation.sku}
                  className="grid gap-2 px-3 py-3 hover:bg-gray-50 items-center text-lg"
                  style={{
                    gridTemplateColumns: `minmax(36px, 44px) minmax(80px, 1fr) repeat(${numVariationTypeColumns}, minmax(60px, 1fr)) minmax(70px, 1fr) minmax(50px, 1fr) minmax(80px, 1fr) minmax(40px, 1fr)`
                  }}
                >
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300"
                      checked={selectedSkus.has(variation.sku)}
                      onChange={() => toggleSkuSelected(variation.sku)}
                      aria-label={`Sélectionner ${variation.sku}`}
                    />
                  </div>
                  <div>{variation.sku}</div>
                  {variationTypes.map((t) => {
                    const typeValue = variation.typeValues.find((tv) => tv.type.id === t.id);
                    return (
                      <div key={t.id}>
                        {typeValue ? getTranslatedValue(typeValue.value) : '-'}
                      </div>
                    );
                  })}
                  <div>
                    {formatCurrency(variation.calculatedCost.find(cc => cc.destinationCountry === 'FR')?.totalCostIncludingVAT ?? 0)}
                  </div>
                  <div>
                    {variation.availableStock.find(s => s.destinationCountry === 'FR')?.availableStock ?? '-'}
                  </div>
                  <div>
                    <DimensionsAndWeightPopover
                      dimensionsAndWeight={{
                        weight: variation.packaging?.weight ?? 0,
                        size1: variation.packaging?.size1 ?? 0,
                        size2: variation.packaging?.size2 ?? 0,
                        size3: variation.packaging?.size3 ?? 0
                      }}
                      onChange={(dimensionsAndWeight) => {
                        handleUpdateVariation(variation.sku, 'packaging', dimensionsAndWeight);
                      }}
                    />
                  </div>
                  <div>
                    <Button
                      preset="danger-light"
                      onClick={() => handleDeleteVariation(variation.sku)}
                    >
                      <MdClose size={20} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>Aucune variation générée</p>
            <p className="text-sm">Créez des types et valeurs, puis cliquez sur &quot;Générer toutes les variations&quot;</p>
          </div>
        )}
      </div>
    </div>
  </>);
} 